import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "./apiFetch";

const hiddenKey = (email) => `linkedinHidden:${email}`;

/**
 * Local-only "disconnect". The backend still holds the tokens — this just
 * stops the app showing the connection for this user on this browser.
 * Remove both of these once the backend revoke endpoint exists.
 */
export const hideLinkedInConnection = (email) => {
  if (email) localStorage.setItem(hiddenKey(email), "true");
};

export const clearLinkedInHidden = (email) => {
  if (email) localStorage.removeItem(hiddenKey(email));
};

/**
 * The single source of truth for "is this user connected to LinkedIn".
 *
 * Connection is derived from the backend, keyed by the signed-in email —
 * never stored in the browser. That means it's correct on any device, and
 * signing in as a different user can't inherit someone else's state.
 *
 * status: "loading" | "connected" | "disconnected" | "error"
 * The last two are deliberately distinct: a backend outage should not tell
 * the user to reconnect an account that is perfectly fine.
 */
export default function useLinkedInAccounts(email) {
  const [accounts, setAccounts] = useState([]);
  const [status, setStatus] = useState("loading");

  const load = useCallback(async () => {
    if (!email) {
      setAccounts([]);
      setStatus("loading");
      return;
    }
    setStatus("loading");
    try {
      const res = await apiFetch(process.env.REACT_APP_LINKEDIN_PROFILE_URL);

      // No tokens stored for this email.
      if (res.status === 400 || res.status === 404) {
        setAccounts([]);
        setStatus("disconnected");
        return;
      }
      if (!res.ok) {
        setAccounts([]);
        setStatus("error");
        return;
      }

      const data = await res.json();
      const orgs = (data.organizations || []).map((org) => ({
        label: org.name,
        target: org.target,
        urn: org.urn,
      }));

      // A 200 with nothing in it still means not connected.
      if (!data?.personal?.urn && orgs.length === 0) {
        setAccounts([]);
        setStatus("disconnected");
        return;
      }

      setAccounts([
        {
          label: data.personal?.name || "Personal Profile",
          target: data.personal?.target || "personal",
          urn: data.personal?.urn || "",
        },
        ...orgs,
      ]);
      setStatus("connected");
    } catch (err) {
      console.error("LinkedIn profile fetch failed:", err);
      setAccounts([]);
      setStatus("error");
    }
  }, [email]);

  useEffect(() => {
    load();
  }, [load]);

  const hidden = !!(email && localStorage.getItem(hiddenKey(email)));

  return {
    accounts: hidden ? [] : accounts,
    status: hidden ? "disconnected" : status,
    isConnected: !hidden && status === "connected",
    refresh: load,
  };
}